import { FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"

import { IconName } from "../Icon"
import { DisplayModeFormButtons, FormButtonArea } from "./FormButtonArea"
import GeneralFormError from "./GeneralFormError"

interface IProps {
  className?: string
  classNameButton?: string
  titleSubmitButton?: string
  iconNameSubmitButton?: IconName
  excludedFieldsGeneralErrorForm: string[]
  prefixGeneralErrorForm: string
  /**
   * is used to get description for the general displayed error, e.g. "Measurement of the goal" mit dem Fehler: "The entered text is too short."
   * would be displayed together: "Measurement of the goal: The entered text is too short."
   * This is necessary for arrays.
   */
  suffixGeneralErrorForm?: string
  /**
   * used format:
   *
   * @see IValidationErrors
   */
  errors: FormikErrors<any>
  isSubmitting: boolean
  isValidating: boolean
  /**
   * By default the cancel button works as a history back button.
   * Overwrite this if the user should be send somewhere else,
   * e.g. when the form must be closed instead the user must be routed.
   */
  onCancel?: () => void
  displayMode?: DisplayModeFormButtons
}

/**
 * This function produces the "name" of the first error in a given error-list
 * in the form "goals[2].measurability" or just "shortDescription".
 * This form of name is to be used by document.getElementsByName() to identify the element
 * by this name.
 *
 * It may be used to jump the element, when an error occured, e.g.:
 * document.getElementsByName(fullFirstErrorName(errors))[0]?.scrollIntoView()
 *
 * @param errors FormikErrors that occure
 * @returns the full name of the first error in a list of FormikErrors
 */
export const fullFirstErrorName = (errors: FormikErrors<any>): string => {

  // variants of errors:
  // single: "title"
  // array: "impact.2" (may also be an object -> coder-dependent)
  // multi-array as object: "goals[2].measurability"

  if (!errors || errors === undefined) {
    return
  }
  // key of the first error in the givenerror-array
  const firstErrorKey = Object.keys(errors)[0]
  if (!firstErrorKey) {
    return
  }
  // if the value to that key is a string, its a "flat" error, so the firstErrorKey is the error name...
  if (typeof errors[firstErrorKey] === "string") {
    return firstErrorKey
  }

  if (Array.isArray(errors[firstErrorKey])) {
    // may be an array, if a coder defines it as array, not as object
    // ... otherwise there are sub-errors from FormikFieldArray
    const firstError: string[] = errors[firstErrorKey] as string[]
    const elementIndex = firstError[0]
    // if the sub-error is "flat", the errorName has the format name.index, e.g.: "impact.2"
    return firstErrorKey + "." + elementIndex
  }

  if (typeof errors[firstErrorKey] === "object") {
    // if the sub-error is not "flat", its a MultiFieldArray with three levels
    // OR a string-indexed error object
    // attention: may also be an array

    const firstError: Record<string, any> = errors[firstErrorKey] as Record<string, any>
    // get the index of the first element in that error-sub-array
    const elementIndex = Object.keys(firstError)[0]
    // because the error could occur in the third element of that array
    const subError = firstError[elementIndex]

    if (typeof subError === "string") {
      // its an array, although it looks like an object
      return firstErrorKey + "." + elementIndex
    }

    // else get the key of that first sub-error
    const subElementKey = Object.keys(subError as [])[0]
    // build the complete errorName, format name[index].subname, e.g. "goals[2].measurability"
    return firstErrorKey + "[" + elementIndex + "]." + subElementKey
  }

  return undefined
}

/**
 * Display errors for formik as well as a submit button. In order to work, you have place it as a child within a form of formik.
 * As a recommendation, you should place this component at the top as well as at the bottom of your form.
 * The general errors are displayed near the save buttons.
 *
 * Where it is used?
 * On big forms, which are directly displayed at a page.
 * Where it is not used?
 * It is not used in modal, which are using forms. Also not used in Searchforms like ProjectSearchForm or UserSearchForm.
 * Also not used in ProjectCreationForm or in the ApplicationPage (MemberApplicationForm).
 * Also it is not used in any forms in the user UI like ProfileView, ValidationForm.
 */
const GeneralButtonAndErrorArea = (props: IProps): JSX.Element => {
  const { t } = useTranslation("common")
  const {
    className,
    classNameButton,
    titleSubmitButton,
    iconNameSubmitButton,
    excludedFieldsGeneralErrorForm,
    prefixGeneralErrorForm,
    suffixGeneralErrorForm = "label",
    errors,
    isSubmitting,
    isValidating,
    onCancel = null,
    displayMode = DisplayModeFormButtons.IconAndTitle
  } = props
  const router = useRouter()

  // state to store, if this component is the sender of the submit-signal
  // to only once trigger the jump to the error-element, when the GeneralButtonAndErrorArea
  // is used more than once in forms
  // @todo: reset the state, because the user may change the used button
  const [isSubmitSender, setIsSubmitSender] = useState(false)

  /*
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const toggleTooltip = () => {
      setTooltipOpen(!tooltipOpen)
  }
*/


  const scrollIntoViewFirstError = () => {
    // check, if the application element is running on the server or in the browser currently => window is just in the browser defined
    if (typeof window !== undefined) {
      const firstErrorName = fullFirstErrorName(errors)
      // The formik components are identified by the name
      // But it may not be supported (suspect: RTE/reactQuill),
      // so identify the element alternatively by its ID
      // NOTE: for tests, mock scrollIntoView by: window.HTMLElement.prototype.scrollIntoView = jest.fn()
      if (document.getElementsByName(firstErrorName).length > 0) {
        // scroll to the first found element
        document.getElementsByName(firstErrorName)[0]?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      } else {
        document.getElementById(firstErrorName)?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      }
    }
  }

  useEffect(() => {
    // If during the validation process are any errors found or the submission was successful, isValidating and isSubmitting is set to false
    // If isValidating and isSubmitting is set to false, it mean's the validation process was running (or was not started yet).
    // After that, the component can check for errors, cause it's important, that validation has been run and therefor the errors have been updated.
    // For more information about formik submission life-cycle look at: https://formik.org/docs/guides/form-submission.
    // But in both use cases (we can't differentiate between both use cases and it is not important),
    // the component is checking for errors and if one or more errors are found, will jump to the first error
    if (!isSubmitting && !isValidating && isSubmitSender && errors && Object.keys(errors).length > 0) {
      // get the first element with the name of the error-producing element
      // and scroll the users browser window there
      scrollIntoViewFirstError()
    }
  }, [isValidating, isSubmitting])

  return <div className={className ? className : " save-or-cancel"}>
    <div id="errorMessage" onClick={() => {
      scrollIntoViewFirstError()
    }}>
      {Object.keys(errors).length > 0 && <GeneralFormError
        errors={errors}
        prefix={prefixGeneralErrorForm}
        suffixForErrorTitle={suffixGeneralErrorForm ? suffixGeneralErrorForm : undefined}
        excludeFields={excludedFieldsGeneralErrorForm}
      />
      }
    </div>
    <FormButtonArea
      className={classNameButton}
      isSubmitting={isSubmitting}
      submitButton={{
        title: titleSubmitButton ? t(titleSubmitButton) : t("form.saveChanges"),
        onClick: () => setIsSubmitSender(true),
        iconName: iconNameSubmitButton ? iconNameSubmitButton : "save",
        displayMode
      }}
      cancelButton={{
        onClick: onCancel ? onCancel : () => router.back(),
        displayMode
      }}
    />
    {/* // just a design study: no decision if used, FCP-992
    errors &&
            <Tooltip target="errorMessage" toggle={toggleTooltip} isOpen={tooltipOpen}>
                {t("goto.jumpToError")}
            </Tooltip>
        */}
  </div>
}

export default GeneralButtonAndErrorArea